/** Cross-app unit-of-measure + quantity formatting (negocio · portal · rider). */

export type UnitOfMeasure = 'unit' | 'kg' | 'g' | 'l' | 'ml' | 'hour' | 'm' | 'cm';

export const UNIT_OF_MEASURE_VALUES: readonly UnitOfMeasure[] = [
  'unit',
  'kg',
  'g',
  'l',
  'ml',
  'hour',
  'm',
  'cm',
] as const;

const ALLOWED = new Set<string>(UNIT_OF_MEASURE_VALUES);

/** Options for catalog product form (all CHECK-allowed UoMs). */
export const UOM_OPTIONS: ReadonlyArray<{ id: UnitOfMeasure; label: string }> = [
  { id: 'unit', label: 'Unidad' },
  { id: 'kg', label: 'Kg' },
  { id: 'g', label: 'g' },
  { id: 'l', label: 'L' },
  { id: 'ml', label: 'ml' },
  { id: 'hour', label: 'Hora' },
  { id: 'm', label: 'm' },
  { id: 'cm', label: 'cm' },
];

export function normalizeUnitOfMeasure(raw: unknown): UnitOfMeasure {
  const value = String(raw ?? 'unit').trim();
  return ALLOWED.has(value) ? (value as UnitOfMeasure) : 'unit';
}

export function formatUnitLabel(uom: UnitOfMeasure): string {
  switch (normalizeUnitOfMeasure(uom)) {
    case 'kg':
      return 'kg';
    case 'g':
      return 'g';
    case 'l':
      return 'L';
    case 'ml':
      return 'ml';
    case 'hour':
      return 'hs';
    case 'm':
      return 'm';
    case 'cm':
      return 'cm';
    default:
      return 'u.';
  }
}

/** True when qty may be fractional (everything except discrete `unit`). */
export function quantityAllowsFraction(uom?: UnitOfMeasure | string | null): boolean {
  return normalizeUnitOfMeasure(uom) !== 'unit';
}

function formatQtyNumber(quantity: number, maxFractionDigits = 3): string {
  const abs = Math.abs(quantity);
  if (Number.isInteger(abs) || Math.abs(abs - Math.round(abs)) < 1e-9) {
    return String(Math.trunc(quantity));
  }
  return quantity.toLocaleString('es-AR', { maximumFractionDigits: maxFractionDigits });
}

/** User-facing MOQ label: "Mínimo 2 kg" / "Mínimo 6 u." */
export function formatMinOrderLabel(
  quantity: number,
  uom?: UnitOfMeasure | string | null,
): string {
  return `Mínimo ${formatQty(quantity, uom)}`;
}

/** User-facing max-qty label: "Máximo 10 kg" */
export function formatMaxOrderLabel(
  quantity: number,
  uom?: UnitOfMeasure | string | null,
): string {
  return `Máximo ${formatQty(quantity, uom)}`;
}

/** Combine product max order qty with stock cap when both exist. */
export function effectiveMaxOrderQty(
  maxOrderQty: number | null | undefined,
  stockCap: number | null | undefined,
): number | null {
  if (maxOrderQty == null && stockCap == null) return null;
  if (maxOrderQty == null) return stockCap ?? null;
  if (stockCap == null) return maxOrderQty;
  return Math.min(maxOrderQty, stockCap);
}

/** `2,5 kg` / `12 u.` */
export function formatQty(
  quantity: number,
  uom?: UnitOfMeasure | string | null,
  opts?: { maxFractionDigits?: number },
): string {
  const unit = formatUnitLabel(normalizeUnitOfMeasure(uom));
  return `${formatQtyNumber(quantity, opts?.maxFractionDigits ?? 3)} ${unit}`;
}

/**
 * Cart compact: bare `3` for unit; `0,5 kg` for fractional UoMs.
 */
export function formatQtyCompact(
  quantity: number,
  uom?: UnitOfMeasure | string | null,
): string {
  const normalized = normalizeUnitOfMeasure(uom);
  if (normalized === 'unit') {
    return formatQtyNumber(quantity);
  }
  return formatQty(quantity, normalized);
}

/** Bare qty for `<input>` display — `es-AR` comma (parse still accepts comma or dot). */
export function formatQtyInputValue(
  quantity: number,
  uom?: UnitOfMeasure | string | null,
  opts?: { maxFractionDigits?: number },
): string {
  if (!quantityAllowsFraction(uom)) return String(Math.trunc(quantity));
  return formatQtyNumber(quantity, opts?.maxFractionDigits ?? 3);
}

export type ParseQtyInputOptions = {
  allowNegative?: boolean;
  min?: number;
  max?: number;
};

/**
 * Suggested +/- step for portal/POS (no `order_qty_step` in catalog yet).
 * When `minOrderQty` is set, it doubles as the increment hint.
 */
export function defaultQuantityStep(
  uom?: UnitOfMeasure | string | null,
  minOrderQty?: number | null,
): number {
  const normalized = normalizeUnitOfMeasure(uom);
  if (normalized === 'unit') return 1;
  if (minOrderQty != null && minOrderQty > 0) return minOrderQty;
  switch (normalized) {
    case 'kg':
      return 0.5;
    case 'g':
      return 100;
    case 'l':
      return 0.25;
    case 'ml':
      return 250;
    case 'm':
      return 0.1;
    case 'cm':
      return 10;
    case 'hour':
      return 0.5;
    default:
      return 0.5;
  }
}

/** Strip invalid chars while typing a decimal qty (es-AR comma/dot). */
export function sanitizeDecimalQtyInput(raw: string): string {
  let out = '';
  let sep = false;
  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') {
      out += ch;
      continue;
    }
    if ((ch === ',' || ch === '.') && !sep) {
      out += ch;
      sep = true;
    }
  }
  return out;
}

/** Parse user qty text (es-AR: comma or dot decimal). Integer-only when UoM is `unit`. */
export function parseQtyInput(
  raw: string,
  uom?: UnitOfMeasure | string | null,
  opts?: ParseQtyInputOptions,
): number | null {
  const integerOnly = !quantityAllowsFraction(uom);
  let normalized = raw.trim().replace(/\$/g, '').replace(/\s/g, '');
  if (!normalized) return null;

  if (normalized.includes(',')) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = normalized.replace(',', '.');
  }

  if (normalized.includes('-')) {
    const minusCount = (normalized.match(/-/g) ?? []).length;
    if (minusCount > 1 || (minusCount === 1 && !normalized.startsWith('-'))) {
      return null;
    }
  }

  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  if (!opts?.allowNegative && value < 0) return null;

  if (integerOnly) {
    const frac = Math.abs(value % 1);
    if (frac > 1e-9) return null;
  }

  const finalValue = integerOnly ? Math.trunc(value) : value;
  if (opts?.min != null && finalValue < opts.min) return null;
  if (opts?.max != null && finalValue > opts.max) return null;
  return finalValue;
}

/** Kind hint for RN NumericTextInput / localeNumeric. */
export function numericKindForQty(
  uom?: UnitOfMeasure | string | null,
  opts?: { signed?: boolean },
): 'integer' | 'decimal' | 'signedInteger' | 'signedDecimal' {
  const frac = quantityAllowsFraction(uom);
  if (opts?.signed) return frac ? 'signedDecimal' : 'signedInteger';
  return frac ? 'decimal' : 'integer';
}

export function productRequiresWeightInput(product: {
  unitOfMeasure?: UnitOfMeasure | string | null;
}): boolean {
  const uom = normalizeUnitOfMeasure(product.unitOfMeasure);
  return uom === 'kg' || uom === 'g';
}

export function productRequiresHourInput(product: {
  unitOfMeasure?: UnitOfMeasure | string | null;
}): boolean {
  return normalizeUnitOfMeasure(product.unitOfMeasure) === 'hour';
}

/**
 * Gap 1: true para cualquier UoM fraccional que no sea `unit`.
 * Cubre kg, g, l, ml, m, cm, hour.
 * Usado por el POS para decidir si abrir el modal de cantidad al tocar un producto.
 * No incluye `hour` si se quiere rutear al modal de horas por separado
 * — usar `productRequiresHourInput` antes de esta función.
 */
export function productRequiresQuantityInput(product: {
  unitOfMeasure?: UnitOfMeasure | string | null;
}): boolean {
  return quantityAllowsFraction(product.unitOfMeasure);
}

export function computeLineTotal(
  price: number,
  quantity: number,
  uom: UnitOfMeasure | string | null | undefined,
): number {
  const normalized = normalizeUnitOfMeasure(uom);
  if (normalized === 'kg' || normalized === 'g') {
    return Math.round(price * quantity * 100) / 100;
  }
  return Math.round(price * quantity);
}

/** Alias used by POS cart fractional checks. */
export function isFractionalQtyLine(item: {
  unitOfMeasure?: UnitOfMeasure | string | null;
}): boolean {
  return quantityAllowsFraction(item.unitOfMeasure);
}

/** Derived column for DB/sync — true iff UoM is weight. */
export function sellByWeightFromUnit(uom?: UnitOfMeasure | string | null): boolean {
  return productRequiresWeightInput({ unitOfMeasure: uom });
}
